import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Footer from "../components/Footer";
import SiteHeader from "../components/SiteHeader";
import { setSubscriberActiveByEmail } from "../firebase";

const normalizeEmail = (value) => `${value || ""}`.trim().toLowerCase();

const isValidAction = (value) => value === "unsubscribe" || value === "resubscribe";

function SubscriptionManagePage() {
  const [searchParams] = useSearchParams();
  const email = normalizeEmail(searchParams.get("email"));
  const action = `${searchParams.get("action") || ""}`.trim().toLowerCase();
  const [status, setStatus] = useState({
    type: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isActionRequest = isValidAction(action);

  const submitAction = useCallback(async (nextAction) => {
    if (!email) {
      setStatus({
        type: "error",
        message: "Missing email address in this link.",
      });
      return;
    }

    const shouldBeActive = nextAction === "resubscribe";
    setIsSubmitting(true);
    setStatus({
      type: "",
      message: "",
    });

    try {
      await setSubscriberActiveByEmail({
        email,
        active: shouldBeActive,
      });

      setStatus({
        type: "success",
        message: shouldBeActive
          ? "You are resubscribed. You'll receive upcoming AI Lab emails again."
          : "You are unsubscribed. You won't receive future AI Lab emails.",
      });
    } catch (error) {
      console.error("Failed to update subscription preference:", error);
      setStatus({
        type: "error",
        message: "We couldn't update your preference right now. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [email]);

  useEffect(() => {
    if (!isActionRequest) {
      return;
    }

    submitAction(action);
  }, [action, isActionRequest, submitAction]);

  const description = useMemo(() => {
    if (!email) {
      return "This link is missing your email address.";
    }

    if (action === "unsubscribe") {
      return `Updating your preference for ${email}.`;
    }

    if (action === "resubscribe") {
      return `Reactivating updates for ${email}.`;
    }

    return `Manage email preferences for ${email}.`;
  }, [action, email]);

  return (
    <>
      <SiteHeader showRegisterButton={false} />
      <main className="section">
        <div className="container narrow">
          <article className="card">
            <h1>Email Preferences</h1>
            <p>{description}</p>

            {isActionRequest ? (
              <p className="muted-copy">{isSubmitting ? "Saving..." : "Done."}</p>
            ) : null}

            {status.message ? (
              <p className={`status-message ${status.type}`}>{status.message}</p>
            ) : null}

            {!isActionRequest ? (
              <div className="action-row">
                <button
                  className="btn btn-outline"
                  type="button"
                  onClick={() => submitAction("unsubscribe")}
                  disabled={isSubmitting}
                >
                  Unsubscribe
                </button>
                <button
                  className="btn btn-primary"
                  type="button"
                  onClick={() => submitAction("resubscribe")}
                  disabled={isSubmitting}
                >
                  Resubscribe
                </button>
              </div>
            ) : action === "unsubscribe" ? (
              <div className="action-row">
                <button
                  className="btn btn-primary"
                  type="button"
                  onClick={() => submitAction("resubscribe")}
                  disabled={isSubmitting}
                >
                  Resubscribe
                </button>
              </div>
            ) : (
              <div className="action-row">
                <button
                  className="btn btn-outline"
                  type="button"
                  onClick={() => submitAction("unsubscribe")}
                  disabled={isSubmitting}
                >
                  Unsubscribe
                </button>
              </div>
            )}

            <p className="muted-copy">
              <Link to="/">Return to AI Lab home</Link>
            </p>
          </article>
        </div>
      </main>
      <Footer />
    </>
  );
}

export default SubscriptionManagePage;
